// src/pages/Features.jsx
import React from "react";
import {
  Container,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { motion } from "framer-motion";

import AnalyticsIcon from "@mui/icons-material/Analytics";
import TokenIcon from "@mui/icons-material/Token";
import ChatIcon from "@mui/icons-material/Chat";
import StarsIcon from "@mui/icons-material/Stars";

import "../styles/Features.css";

/* ================= Brand Glass (violet + blue) ================= */
const GLASS = {
  bg: "linear-gradient(135deg, rgba(12,16,26,.96) 0%, rgba(4,7,16,.98) 100%)",
  surface:
    "linear-gradient(180deg, rgba(14,19,33,.95) 0%, rgba(6,9,18,.97) 70%)",
  border: "1px solid rgba(255,255,255,.06)",
  borderHover: "1px solid rgba(255,255,255,.18)",
  shadow: "0 18px 46px rgba(2,8,23,.65)",
  text: "#e5e7eb",
  subtext: "rgba(156,163,175,.96)",
  accentFrom: "#a855f7", // фиолетовый
  accentMid: "#6366f1",
  accentTo: "#0ea5e9",   // синий
};

export default function Features() {
  const features = [
    {
      title: "Analytics with AI",
      description: [
        "The Role of AI:",
        "- Personal assistant in launching analytics products.",
        "- Quick analysis of assets for purchase with detailed analysis of potential and risks.",
        "- Recommendations for choosing a market for Predictions trading.",
        "- Automation of analytical Predictions.",
        "In the pro version:",
        "- Rich data flow for asset analytics.",
        "- Unique indicators and metrics for quick market analysis.",
        "- Possibility of connecting AI to exchanges via API to analyze your actions.",
      ],
      icon: <AnalyticsIcon fontSize="large" />,
      link: "https://app.promospay.ai/market",
    },
    {
      title: "Monetization",
      description: [
        "Users and companies receive and pay with digital tokens on the blockchain.",
        "How does this work:",
        "- The user receives tokens for actions on the platform.",
        "- The user also receives bonus tokens for actions on the platform (these can be exchanged for real tokens or used to pay for other platform features).",
        "- Clients who receive PRO status will be able to create their own Forecasts and their own AI models for monetization from companies and the platform, as well as receive commissions.",
        "- Users and the author of the forecasts can support or be against the analytical forecast and buy and share the profit of tokens Yes/No",
        "- Use our AI-powered analytical products, trade, and stake to optimize profits.",
      ],
      icon: <TokenIcon fontSize="large" />,
      link: "https://app.promospay.ai",
    },
    {
      title: "Creating and Trading Predictions",
      description: [
  "Predictions are created and traded directly on the PromosPay platform.",
  "How it works:",
  "- A business/company or an advanced user creates a prediction market: defines the question, outcomes (YES/NO or multi-option), end date and reward pool (manually or with AI templates).",
  "- After approval, the market goes live as on-chain YES/NO tokens that can be bought and sold at any time.",
  "- Prices of outcome tokens change with demand and AI-based probability scores, giving a live implied chance for each scenario.",
  "- When the event is resolved (via oracle or our team), winning outcome tokens are redeemed for full value and rewards, while the market creator earns fees and reputation."
],

      icon: <ChatIcon fontSize="large" />,
      link: "https://app.promospay.ai",
    },
    {
     title: "Token PRO Rewards",
description: [
  "Earn rewards in the PRO token for your activity on the PromosPay platform.",
  "How does this work:",
  "- Connect your wallet and start using PromosPay AI models and prediction markets.",
  "- Place stakes, trade prediction tokens and buy subscriptions inside the app.",
  "- Tip AI presenters, use Boosts and participate in partner campaigns.",
  "- All your actions generate PRO rewards that you can reinvest in the ecosystem or withdraw."
],

      icon: <StarsIcon fontSize="large" />,
      link: "https://app.promospay.ai/client-dashboard",
    },
  ];

  const metricVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 6, md: 8 },
        // фон как у Home: тёмный плюс мягкие фиолетово-синие пятна
        background:
          "radial-gradient(900px 500px at 0% 0%, rgba(168,85,247,.22), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(14,165,233,.18), transparent 55%)",
      }}
    >
      <Container maxWidth="xl">
        {/* Intro */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Bruno Ace SC', sans-serif",
              letterSpacing: ".3px",
              mb: 1.5,
              background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentMid}, ${GLASS.accentTo})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Discover Our Features
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "1rem", md: "1.12rem" },
              color: GLASS.subtext,
              maxWidth: 760,
              mx: "auto",
            }}
          >
            Discover the power of AI and blockchain with PromosPay. Explore
            innovative AI tools, promotion automation, and rewards.
          </Typography>
        </Box>

        {/* Features Grid — glass cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 3,
            alignItems: "stretch",
            mb: 6,
          }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -6 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: index * 0.06 }}
              style={{ height: "100%" }}
            >
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  background: GLASS.surface,
                  borderRadius: 18,
                  p: 2.5,
                  border: GLASS.border,
                  boxShadow: GLASS.shadow,
                  color: GLASS.text,
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  transition:
                    "border-color .25s, box-shadow .25s, transform .25s",
                  "&:hover": {
                    border: GLASS.borderHover,
                    boxShadow: "0 22px 52px rgba(2,8,23,.75)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Icon ring */}
                <Box sx={{ textAlign: "center", mb: 1.5 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: `conic-gradient(from 160deg, ${GLASS.accentFrom}, ${GLASS.accentMid}, ${GLASS.accentTo}, ${GLASS.accentFrom})`,
                      boxShadow:
                        "0 14px 30px rgba(15,23,42,.85), 0 0 0 1px rgba(148,163,184,.4)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 54,
                        height: 54,
                        borderRadius: "50%",
                        background:
                          "radial-gradient(circle at 30% 0%, rgba(255,255,255,.12), transparent 60%), rgba(10,16,28,1)",
                        color: GLASS.text,
                        border: "1px solid rgba(148,163,184,.45)",
                      }}
                    >
                      {feature.icon}
                    </Box>
                  </Box>
                </Box>

                {/* Title */}
                <Typography
                  variant="h6"
                  align="center"
                  sx={{
                    fontSize: { xs: "1.15rem", md: "1.25rem" },
                    fontWeight: 800,
                    pb: 1,
                    mb: 1,
                    borderBottom: "1px dashed rgba(148,163,184,.35)",
                    fontFamily: "'Bruno Ace SC', sans-serif",
                    color: GLASS.text,
                  }}
                >
                  {feature.title}
                </Typography>

                {/* Description lines  */}
                <Box sx={{ flex: 1 }}>
                  {feature.description.map((line, i) => (
                    <Typography
                      key={i}
                      variant="body2"
                      sx={{
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.02))",
                        color: GLASS.subtext,
                        p: 1,
                        borderRadius: "8px",
                        mt: 1,
                        border: "1px solid rgba(148,163,184,.32)",
                        fontSize: { xs: ".86rem", md: ".92rem" },
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>

                {/* Button */}
                <Button
                  variant="outlined"
                  onClick={() => (window.location.href = feature.link)}
                  sx={{
                    mt: 2,
                    mx: "auto",
                    color: GLASS.text,
                    borderColor: "rgba(148,163,184,.7)",
                    fontFamily: "'Bruno Ace SC', sans-serif",
                    textTransform: "none",
                    px: 2.5,
                    borderRadius: 999,
                    background:
                      "radial-gradient(circle at 0% 0%, rgba(168,85,247,.18), transparent 65%)",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(168,85,247,.35), rgba(14,165,233,.35))",
                      borderColor: "#e5e7eb",
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* CTA — glass */}
        <Box
          sx={{
            textAlign: "center",
            mt: 8,
            background: GLASS.bg,
            border: GLASS.border,
            borderRadius: 18,
            boxShadow: GLASS.shadow,
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
            backdropFilter: "blur(12px) saturate(130%)",
            WebkitBackdropFilter: "blur(12px) saturate(130%)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Bruno Ace SC', sans-serif",
              mb: 1.5,
              background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.08rem",
              color: GLASS.subtext,
              mb: 3,
              maxWidth: "720px",
              mx: "auto",
            }}
          >
            Join PromosPay today and transform your assets into tokenized
            opportunities with AI-driven insights.
          </Typography>
          <Button
            variant="contained"
            onClick={() => (window.location.href = "https://app.promospay.ai")}
            sx={{
              padding: "12px 24px",
              fontSize: "1.05rem",
              fontFamily: "'Bruno Ace SC', sans-serif",
              background: `linear-gradient(90deg, ${GLASS.accentFrom} 0%, ${GLASS.accentTo} 100%)`,
              boxShadow: "0 16px 40px rgba(15,23,42,.9)",
              borderRadius: 999,
              textTransform: "uppercase",
              color: "#0b0f14",
              "&:hover": {
                boxShadow: "0 22px 52px rgba(15,23,42,1)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Start Now
          </Button>
        </Box>

        {/* Metrics — glass tiles */}
        <Box sx={{ mt: 8, mb: 2, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Bruno Ace SC', sans-serif",
              color: GLASS.text,
              mb: 3.5,
            }}
          >
            Our Achievements
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            {[
              { value: "12+ Terabyte", label: "Collected unique datasets" },
              { value: "10,000+", label: "Active Users" },
              { value: "20+", label: "Countries Covered" },
              { value: "$1M+", label: "Rewards Distributed" },
            ].map((m, i) => (
              <motion.div
                key={i}
                variants={metricVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 16,
                    background: GLASS.surface,
                    border: GLASS.border,
                    boxShadow: GLASS.shadow,
                    color: GLASS.text,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      fontFamily: "'Bruno Ace SC', sans-serif",
                      mb: 0.75,
                      background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {m.value}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.05rem",
                      fontFamily: "'Bruno Ace SC', sans-serif",
                      color: GLASS.subtext,
                    }}
                  >
                    {m.label}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
